import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useCreateCustomGroup } from 'chat/conversations/group/context'
import { useCompaniesContext } from 'company/context'
import { useAwardGoldStars } from 'gold_stars/queries/award'
import { useTransferGoldStars } from 'gold_stars/queries/transfer'
import { AwardTransferGoldStarScreenProps } from 'home/stack/types'
import { useCallback, useLayoutEffect, useState } from 'react'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { useUser } from 'users/queries/detail'
import { AwardTransferGoldStarView } from './views'

export function AwardTransferGoldStarScreen({
  navigation,
  route,
}: AwardTransferGoldStarScreenProps) {
  const { data: user } = useUser()
  const { activeCompany } = useCompaniesContext()
  const awardGoldStarsMutation = useAwardGoldStars(activeCompany?.id)
  const transferGoldStarsMutation = useTransferGoldStars(activeCompany?.id)
  const { members, setMembers } = useCreateCustomGroup()

  const { handleNavigation } = useRoutingContext()

  const [loading, setLoading] = useState<boolean>(false)
  const [sendMessageToCompanyGroup, setSendMessageToCompanyGroup] =
    useState<boolean>(true)
  const [sendMessageToStoreGroups, setSendMessageToStoreGroups] =
    useState<boolean>(false)
  const [optionalMessage, setOptionalMessage] = useState<string | undefined>()
  const [starCount, setStarCount] = useState<string | undefined>()

  useLayoutEffect(() => {
    showHeaderTitle(
      navigation,
      route.params.type === 'award'
        ? t('awardGoldStars')
        : t('transferGoldStars')
    )
  }, [navigation, route.params.type])

  const onAwardTransferGoldStars = useCallback(async () => {
    const isAward = route.params.type === 'award'
    try {
      setLoading(true)
      if (isAward) {
        await awardGoldStarsMutation.mutateAsync({
          toUserIds: Object.values(members).map(member => member.id),
          sendMessageToCompanyGroup: sendMessageToCompanyGroup,
          sendMessageToStoreGroups: sendMessageToStoreGroups,
          optionalMessage: optionalMessage,
        })
      } else {
        await transferGoldStarsMutation.mutateAsync({
          toUserId: Object.values(members)[0].id,
          starCount: Number(starCount) ?? 0,
        })
      }
    } catch (err: any) {
      console.error(err)
      errorAlert(
        err,
        t('errorGoldStarAlert', {
          type: isAward ? t('awarding') : t('transferring'),
        })
      )
    } finally {
      setLoading(false)
      if (isAward) {
        handleNavigation({
          routeName: 'conversations',
          routeParams: {
            screen:
              user?.role === 'observer' || user?.role === 'admin'
                ? 'groups'
                : 'allConversations',
          },
        })
      } else {
        handleNavigation({
          routeName: 'gold_stars',
        })
      }
    }
  }, [
    route.params.type,
    awardGoldStarsMutation,
    members,
    sendMessageToCompanyGroup,
    sendMessageToStoreGroups,
    optionalMessage,
    transferGoldStarsMutation,
    starCount,
    handleNavigation,
    user?.role,
  ])

  return (
    <StatusBoundary isLoading={false} error={undefined}>
      <AwardTransferGoldStarView
        type={route.params.type}
        members={members}
        onMembersChanged={setMembers}
        loading={loading}
        onAwardTransferGoldStars={onAwardTransferGoldStars}
        sendMessageToCompanyGroup={sendMessageToCompanyGroup}
        sendMessageToStoreGroups={sendMessageToStoreGroups}
        setSendMessageToCompanyGroup={setSendMessageToCompanyGroup}
        setSendMessageToStoreGroups={setSendMessageToStoreGroups}
        optionalMessage={optionalMessage}
        setOptionalMessage={setOptionalMessage}
        starCount={starCount}
        setStarCount={setStarCount}
      />
    </StatusBoundary>
  )
}
